/*
 * Where a person sets their own status: the presets, the palette, the line and the moment it clears
 * at, in one dialog opened from the account menu.
 *
 * Three decisions are worth knowing.
 *
 * It saves on submit and writes only what changed. A status rides `account.self.update`, which
 * shares one bucket of thirty writes an hour with every other profile edit, so a picker that wrote
 * on every press would spend a person's allowance on the way to the sentence they meant. The draft
 * therefore lives here until *Save*, and `statusPatch` reduces it to the fields that actually moved.
 *
 * The palette is a **radio group of real radios**. Twenty-five focusable cells would be twenty-five
 * tab stops, and the alternative — a grid with a roving tabindex and arrow keys of its own — is
 * forty lines of keyboard code to re-create what `<input type="radio">` already does: one stop,
 * arrows that walk and choose as they go, and "3 of 25" read out without anybody writing it. The
 * input is hidden from the eye and not from anything else, and the cell beside it wears the ring.
 *
 * The emoji are drawn in the emoji face (`.tb-emoji`), never in the interface face, which carries no
 * colour glyphs — an unwrapped emoji is a tofu box on a machine whose system font has none
 * (X-A1 D12). Each cell carries the emoji's name for a reader who cannot see the picture, and the
 * glyph itself is hidden from assistive technology so the name is not read twice.
 */

import { useId, useState } from "react"
import { useAsyncAction } from "../../lib/useAsyncAction.ts"
import { Button, Dialog, Field, FormAlert, Select } from "../../ui/index.ts"
import { authApi } from "../auth/index.ts"
import { formatDayTime, t } from "../i18n/index.ts"
import { useNow } from "../i18n/useNow.ts"
import { Icon } from "../shell/icons.tsx"
import type { SelfUser } from "../store/model.ts"
import "./status.css"
import {
	CLEAR_AFTER,
	clearAfterLabel,
	clearAfterShown,
	clearedStatus,
	draftOf,
	emojiName,
	MAX_STATUS_TEXT,
	STATUS_PRESETS,
	statusEmojis,
	statusFields,
	statusOf,
	statusPatch,
	withPreset,
	type ClearAfter,
	type StatusDraft,
} from "./status.ts"

interface StatusDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	me: SelfUser
}

export function StatusDialog({ open, onOpenChange, me }: StatusDialogProps) {
	return (
		<Dialog
			open={open}
			onOpenChange={onOpenChange}
			title={t("people.status.title")}
			description={t("people.status.lead")}
		>
			{/* Mounted with the dialog, so the form starts from the stored status every time it opens
			    and never from the draft somebody abandoned an hour ago. */}
			{open ? <StatusForm me={me} onDone={() => onOpenChange(false)} /> : null}
		</Dialog>
	)
}

function StatusForm({ me, onDone }: { me: SelfUser; onDone: () => void }) {
	const stored = statusFields(me)
	const [draft, setDraft] = useState<StatusDraft>(() => draftOf(stored))
	// The end a status already carries is drawn in the option that keeps it, and it ages: the
	// interface's one clock moves it from "13:00" to "18 September 13:00" when midnight passes under
	// an open dialog, together with every other stamp on the screen.
	const now = useNow()
	const paletteName = useId()
	const awayId = useId()
	const awayHintId = `${awayId}-hint`

	const save = useAsyncAction(async () => {
		const patch = statusPatch(stored, draft, Date.now())
		if (Object.keys(patch).length > 0) await authApi.updateProfile(patch)
		onDone()
	})

	const clear = useAsyncAction(async () => {
		await authApi.updateProfile(clearedStatus())
		onDone()
	})

	function edit(change: Partial<StatusDraft>) {
		setDraft((current) => ({ ...current, ...change }))
		save.reset()
		clear.reset()
	}

	const busy = save.busy || clear.busy
	// Whether there is anything to clear, decided by the one rule that decides whether there is
	// anything to draw: an end with no words behind it is not a status (`statusOf`).
	const published = statusOf(me) !== null

	return (
		<form
			className="tb-status-form"
			onSubmit={(event) => {
				event.preventDefault()
				void save.run()
			}}
		>
			<fieldset className="tb-status-form__group">
				<legend className="tb-field__label">{t("people.status.presets")}</legend>
				<div className="tb-status-form__presets">
					{STATUS_PRESETS.map((preset) => (
						<Button
							key={preset.id}
							type="button"
							variant="neutral"
							size="sm"
							disabled={busy}
							before={
								<span className="tb-emoji" aria-hidden="true">
									{preset.emoji}
								</span>
							}
							label={t(preset.label)}
							onClick={() => setDraft((current) => withPreset(current, preset))}
						/>
					))}
				</div>
			</fieldset>

			<fieldset className="tb-status-form__group">
				<legend className="tb-field__label">{t("people.status.emoji")}</legend>
				<div className="tb-status-form__palette">
					{/* The empty emoji leads the palette, because that is what it is on the wire and
					    because a choice with no way out of it is a trap: a status that is only words is
					    an ordinary status. */}
					{["", ...statusEmojis].map((emoji) => (
						<Swatch
							key={emoji}
							name={paletteName}
							emoji={emoji}
							checked={draft.emoji === emoji}
							disabled={busy}
							onPick={() => edit({ emoji })}
						/>
					))}
				</div>
			</fieldset>

			<Field
				label={t("people.status.text")}
				hint={t("people.status.text.hint", { max: MAX_STATUS_TEXT })}
				wide
			>
				{(props) => (
					<input
						{...props}
						type="text"
						maxLength={MAX_STATUS_TEXT}
						disabled={busy}
						value={draft.text}
						onChange={(event) => edit({ text: event.target.value })}
					/>
				)}
			</Field>

			<Field label={t("people.status.clearAfter")}>
				{(props) => (
					<Select
						{...props}
						disabled={busy}
						// Not `draft.clearAfter`: the option that keeps the stored end disappears when the
						// stored status does, and the picker must then show the offered end that means the
						// same as the answer the save will write (`clearAfterShown`).
						value={clearAfterShown(stored, draft)}
						onChange={(event) =>
							edit({ clearAfter: event.target.value as StatusDraft["clearAfter"] })
						}
					>
						{/* Offered only while there is an end to keep, and first, because it is what the
						    dialog opened with: somebody fixing a typo must not have to find the moment
						    they chose this morning again. */}
						{stored.until === 0 ? null : (
							<option value="keep">
								{t("people.status.clearAfter.keep", {
									when: formatDayTime(stored.until, now),
								})}
							</option>
						)}
						{CLEAR_AFTER.map((choice: ClearAfter) => (
							<option key={choice} value={choice}>
								{clearAfterLabel(choice)}
							</option>
						))}
					</Select>
				)}
			</Field>

			<div className="tb-check">
				<input
					id={awayId}
					type="checkbox"
					checked={draft.away}
					disabled={busy}
					aria-describedby={awayHintId}
					onChange={(event) => edit({ away: event.target.checked })}
				/>
				<span>
					<label className="tb-field__label" htmlFor={awayId}>
						{t("people.status.showAway")}
					</label>
					<span className="tb-field__hint" id={awayHintId}>
						{t("people.status.showAway.hint")}
					</span>
				</span>
			</div>

			<FormAlert messageKey={save.error ?? clear.error} />

			<div className="tb-dialog__actions">
				<Button type="button" label={t("action.cancel")} disabled={busy} onClick={onDone} />
				{published ? (
					<Button
						type="button"
						label={t("people.status.clear")}
						busy={clear.busy}
						disabled={busy}
						onClick={() => void clear.run()}
					/>
				) : null}
				<Button
					type="submit"
					variant="primary"
					label={t("action.save")}
					busy={save.busy}
					disabled={busy}
				/>
			</div>
		</form>
	)
}

interface SwatchProps {
	name: string
	/** One member of the palette, or `""` — the choice of no emoji, which is the value the wire uses. */
	emoji: string
	checked: boolean
	disabled: boolean
	onPick: () => void
}

/**
 * One cell of the palette: a real radio, out of sight, and the picture it stands for beside it. The
 * picture is hidden from assistive technology and the name stands beside it, so a reader hears
 * "Headphones" once rather than the emoji's own name in front of it.
 */
function Swatch({ name, emoji, checked, disabled, onPick }: SwatchProps) {
	return (
		<label className="tb-status-swatch">
			<input
				className="tb-visually-hidden"
				type="radio"
				name={name}
				value={emoji}
				checked={checked}
				disabled={disabled}
				onChange={onPick}
			/>
			<span className="tb-status-swatch__glyph" aria-hidden="true">
				{emoji === "" ? <Icon name="close" size={16} /> : <span className="tb-emoji">{emoji}</span>}
			</span>
			<span className="tb-visually-hidden">
				{emoji === "" ? t("people.status.noEmoji") : emojiName(emoji)}
			</span>
		</label>
	)
}
